import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import {
    ArrowUpRight,
    Building2,
    Users,
    Award,
    Briefcase,
    TrendingUp,
    ClipboardCheck,
    Sparkles,
    Linkedin,
    Quote,
    Hotel,
    Utensils,
    KeyRound,
    Building,
    BadgeCheck,
    ShieldCheck,
    MapPinned,
    UserCheck,
    LineChart,
    Smile,
} from "lucide-react";
import { useSEO } from "../lib/useSEO";
import { api } from "../lib/api";
import { organisationSchema, localBusinessSchema } from "../lib/seo";
import { CASE_STUDIES as ALL_CASE_STUDIES } from "../lib/caseStudiesData";

const WHAT = [
    {
        icon: Building2,
        title: "Operational Excellence",
        body: "Streamlining operations across hotels, serviced apartments, restaurants and bars to lift efficiency, consistency and service standards.",
    },
    {
        icon: TrendingUp,
        title: "Performance & Profitability",
        body: "Identifying revenue leakage, sharpening commercial strategy, and optimising channels and pricing to grow contribution across every revenue line.",
    },
    {
        icon: ClipboardCheck,
        title: "SOPs & Quality Assurance",
        body: "Authoring practical SOPs and audit programmes that embed service and brand standards your teams can actually live by.",
    },
];

const SECTORS = [
    { icon: Hotel, label: "Independent Hotels", body: "Owner operated and small group properties where the operating layer carries the business." },
    { icon: Sparkles, label: "Boutique Hotels", body: "Design led properties where service standards and consistency define the guest perception." },
    { icon: BadgeCheck, label: "Branded Hotels", body: "Working alongside brand standards and operating partners to lift the property's performance." },
    { icon: Utensils, label: "Restaurants", body: "Food, beverage and service operations within hotels, restaurants with rooms and standalone venues." },
    { icon: Building, label: "Multi-Site Hospitality Businesses", body: "Small groups and portfolios where consistency, cadence and head office capacity matter." },
    { icon: KeyRound, label: "Serviced Accommodation", body: "Aparthotels and serviced apartment operators where operating models need to scale cleanly." },
];

const TRUST_BAR = [
    { icon: MapPinned, label: "UK Hospitality Consultancy" },
    { icon: UserCheck, label: "Founder Led" },
    { icon: Briefcase, label: "Practical Operational Experience" },
    { icon: ShieldCheck, label: "Nationwide Support" },
];

const CASE_STUDIES = ALL_CASE_STUDIES.slice(0, 3);

const AUTHORITY = [
    { title: "Practical Hospitality Experience", body: "More than a decade running hospitality operations across hotels, serviced apartments, restaurants and multi site businesses, from the service floor through to operations director level." },
    { title: "Performance Improvement Focus", body: "Engagements are built around the levers that actually move GOP, labour productivity, guest scores and contribution. Findings are prioritised by impact, not by volume." },
    { title: "Operational Perspective", body: "Recommendations come from someone who has stood behind reception at the height of a busy weekend, run a kitchen pass and chaired the operations meeting on a Monday morning." },
    { title: "Commercially Minded Approach", body: "Operational decisions are framed against the P&L, the asset and the owner's objectives. The work is grounded in evidence and the management accounts." },
    { title: "Nationwide Support", body: "Based in London, working with hotel owners and operators across England, Scotland, Wales and Northern Ireland with meaningful time spent in the property." },
];

const EXPERIENCE = [
    {
        title: "Multi-site hospitality operations leadership",
        body: "Operational responsibility across several sites at once, coordinating teams, standards and performance.",
    },
    {
        title: "Hotel and serviced apartment operations experience",
        body: "Practical operational leadership across hotels and serviced apartment environments, from front office through to back of house.",
    },
    {
        title: "Restaurant and hospitality business support",
        body: "Operational and service support across restaurants, bars and the wider hospitality sector.",
    },
    {
        title: "Team leadership and development",
        body: "Building, coaching and turning around hospitality teams, from new joiners through to senior managers.",
    },
    {
        title: "Guest experience improvement",
        body: "Mapping and elevating guest journeys to lift satisfaction, repeat custom and reputation scores.",
    },
    {
        title: "Operational standards and compliance",
        body: "Establishing SOPs, brand standards and quality assurance programmes that hold up under audit.",
    },
    {
        title: "Performance and profitability improvement initiatives",
        body: "Identifying operational and commercial levers that move the bottom line, without sacrificing service.",
    },
];

const APPROACH = [
    { n: "01", t: "Understand your business challenges", b: "Every engagement begins with a structured conversation to understand what is really going on in the business." },
    { n: "02", t: "Assess operations and opportunities", b: "On-site or remote review of operations, team performance and the commercial picture, finding the real levers." },
    { n: "03", t: "Deliver practical recommendations", b: "Prioritised recommendations grounded in evidence that fit your team and your timeline, not a generic playbook." },
    { n: "04", t: "Support implementation and results", b: "Stay close through delivery to make sure recommendations turn into change that actually sticks." },
];

const PLACEHOLDER_TESTIMONIALS_REMOVED = [];

export default function HomePage() {
    useSEO({
        title: "Optimum Hospitality Solutions — Hospitality Consultant | Sidney Adams",
        description:
            "Optimum Hospitality Solutions is a London-based hospitality consultancy led by Sidney Adams. Supporting hotels, serviced apartments, restaurants, bars and hospitality operators with operational excellence, guest experience, leadership and business performance.",
        path: "/",
    });

    const [testimonials, setTestimonials] = useState([]);
    const [loadedReal, setLoadedReal] = useState(false);

    useEffect(() => {
        let cancelled = false;
        (async () => {
            try {
                const res = await api.get("/recommendations?published_only=true");
                const published = (res.data || [])
                    .filter((r) => r.permission_to_publish && r.published)
                    .map((r) => ({
                        quote: r.recommendation,
                        name: r.allow_name ? r.name : (r.role || "Industry contact"),
                        role: r.allow_name
                            ? `${r.role}${r.organisation ? ` · ${r.organisation}` : ""}`
                            : r.relationship,
                    }));
                if (!cancelled) {
                    setTestimonials(published.slice(0, 3));
                    setLoadedReal(true);
                }
            } catch (_) {
                if (!cancelled) setLoadedReal(true);
            }
        })();
        return () => { cancelled = true; };
    }, []);

    return (
        <div data-testid="home-page">
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(organisationSchema) }} />
            <script type="application/ld+json" dangerouslySetInnerHTML={{ __html: JSON.stringify(localBusinessSchema) }} />
            {/* HERO */}
            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-6 reveal">
                        <span
                            data-testid="hero-overline"
                            className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium"
                        >
                            UK Hospitality Consultancy
                        </span>
                        <h1
                            data-testid="hero-title"
                            className="font-display text-5xl sm:text-6xl lg:text-7xl leading-[1.02] tracking-tight mt-6 text-brand-navy"
                        >
                            Helping Hotels Improve Performance, Profitability and <em className="italic text-brand-champagne not-italic font-display">Guest Experience</em>.
                        </h1>
                        <p
                            data-testid="hero-subtitle"
                            className="mt-7 text-lg leading-relaxed text-brand-ink/75 max-w-xl"
                        >
                            Practical hospitality consultancy supporting hotels, hospitality businesses and multi site operators across the United Kingdom.
                        </p>

                        {/* Credibility statement */}
                        <div className="mt-10 border-l-2 border-brand-champagne pl-5 max-w-xl" data-testid="hero-credibility">
                            <p className="text-sm text-brand-ink/75 leading-relaxed">
                                Led by <span className="font-medium text-brand-navy">Sidney Adams</span>, an experienced hospitality operator with over a decade running operations across hotels, serviced apartments, restaurants and multi site hospitality businesses.
                            </p>
                        </div>

                        <div className="mt-10 flex flex-wrap items-center gap-5">
                            <Link
                                to="/booking"
                                data-testid="hero-cta-booking"
                                className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-navy text-brand-stone text-sm tracking-wide hover:bg-brand-hover transition-colors"
                            >
                                Book a Consultation
                                <ArrowUpRight size={16} />
                            </Link>
                            <Link
                                to="/services"
                                data-testid="hero-cta-services"
                                className="inline-flex items-center gap-2 px-7 py-3.5 border border-brand-navy text-brand-navy text-sm tracking-wide hover:bg-brand-navy hover:text-brand-stone transition-colors"
                            >
                                View Services
                                <ArrowUpRight size={16} />
                            </Link>
                        </div>
                    </div>

                    <div className="lg:col-span-6 reveal">
                        <div className="relative">
                            <img
                                src="https://images.unsplash.com/photo-1677129667171-92abd8740fa3?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NjZ8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBob3RlbCUyMGludGVyaW9yJTIwbW9kZXJufGVufDB8fHx8MTc4MTEyMjM4Nnww&ixlib=rb-4.1.0&q=85"
                                alt="Luxury hotel interior"
                                className="w-full h-[420px] sm:h-[520px] object-cover"
                                width="1200"
                                height="520"
                                fetchPriority="high"
                            />
                            <div className="absolute -bottom-6 -left-6 hidden sm:block bg-brand-stone border border-brand-border p-6 max-w-[240px]">
                                <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">
                                    Established Practice
                                </div>
                                <div className="font-display text-3xl text-brand-navy mt-1">
                                    10+ Years
                                </div>
                                <div className="text-sm text-brand-ink/70 mt-1">
                                    of hospitality leadership across the UK.
                                </div>
                            </div>
                        </div>
                    </div>
                </div>
            </section>

            {/* TRUST & CREDIBILITY BAR */}
            <section className="border-b border-brand-border bg-brand-stone" data-testid="trust-bar">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-6">
                    <div className="grid grid-cols-2 lg:grid-cols-4 gap-x-8 gap-y-4">
                        {TRUST_BAR.map((t) => (
                            <div
                                key={t.label}
                                data-testid={`trust-bar-${t.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="flex items-center gap-3"
                            >
                                <t.icon className="text-brand-champagne shrink-0" size={18} strokeWidth={1.5} />
                                <span className="text-xs sm:text-sm text-brand-ink/75 leading-snug">{t.label}</span>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* EXPERIENCE ACROSS HOSPITALITY OPERATIONS */}
            <section className="border-b border-brand-border bg-white" data-testid="sectors-strip">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24">
                    <div className="max-w-3xl">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                            Where We Work
                        </span>
                        <h2 className="font-display text-4xl sm:text-5xl mt-5 leading-[1.05] tracking-tight text-brand-navy">
                            Experience across hospitality operations.
                        </h2>
                        <p className="mt-6 text-brand-ink/75 leading-relaxed">
                            Independent and branded hotels, boutique properties, restaurants, serviced accommodation and multi site hospitality businesses across the UK. Every engagement is scoped around the property, the team and the owner.
                        </p>
                    </div>

                    <div className="mt-12 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-px bg-brand-border">
                        {SECTORS.map((s) => (
                            <div
                                key={s.label}
                                data-testid={`sector-${s.label.toLowerCase().replace(/\s+/g, "-")}`}
                                className="bg-brand-stone p-8 hover:bg-white transition-colors"
                            >
                                <s.icon className="text-brand-champagne" size={28} strokeWidth={1.25} />
                                <h3 className="font-display text-lg mt-5 text-brand-navy leading-snug">{s.label}</h3>
                                <p className="mt-3 text-sm text-brand-ink/70 leading-relaxed">{s.body}</p>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHAT I DO */}
            <section className="border-b border-brand-border">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-4">
                            <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                                What We Do
                            </span>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-5 leading-tight text-brand-navy">
                                Operational depth, commercial focus across hospitality.
                            </h2>
                            <p className="mt-6 text-brand-ink/75 leading-relaxed">
                                Consulting engagements grounded in over a decade of leadership in the property across hotels, serviced apartments, restaurants and bars, from the service floor through to ownership level strategy.
                            </p>
                        </div>
                        <div className="lg:col-span-8 grid grid-cols-1 md:grid-cols-3 gap-px bg-brand-border">
                            {WHAT.map((item) => (
                                <div
                                    key={item.title}
                                    data-testid={`what-card-${item.title.toLowerCase().replace(/\s+/g, "-")}`}
                                    className="bg-brand-stone p-8 hover:bg-white transition-colors"
                                >
                                    <item.icon className="text-brand-champagne" size={28} strokeWidth={1.25} />
                                    <h3 className="font-display text-xl mt-5 text-brand-navy">
                                        {item.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-brand-ink/75 leading-relaxed">
                                        {item.body}
                                    </p>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* MEET SIDNEY ADAMS — FOUNDER */}
            <section className="border-b border-brand-border bg-white" data-testid="founder-section">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
                    <div className="lg:col-span-5">
                        <div
                            data-testid="founder-portrait"
                            className="relative w-full max-w-[440px] overflow-hidden border border-brand-border"
                        >
                            <img
                                src="/sidney-adams.jpg"
                                alt="Sidney Adams — Founder, Optimum Hospitality Solutions"
                                className="w-full h-auto aspect-[3/4] object-cover"
                                loading="lazy"
                            />
                            <div className="absolute bottom-0 left-0 right-0 p-5 bg-gradient-to-t from-brand-navy/95 via-brand-navy/70 to-transparent">
                                <div className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne">
                                    Founder
                                </div>
                                <div className="font-display text-2xl text-brand-stone mt-1">
                                    Sidney Adams
                                </div>
                            </div>
                        </div>
                    </div>

                    <div className="lg:col-span-7">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                            Meet Sidney Adams
                        </span>
                        <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] tracking-tight text-brand-navy">
                            Operator first, consultant second.
                        </h2>
                        <div className="mt-4 inline-flex items-center gap-2 text-sm text-brand-ink/70">
                            <span className="h-px w-8 bg-brand-champagne" />
                            <span>Founder & Hospitality Consultant</span>
                        </div>

                        <div className="mt-8 space-y-5 text-brand-ink/80 leading-relaxed">
                            <p>
                                Sidney Adams is an experienced hospitality operator with more than a decade leading teams across hotels, serviced apartments, restaurants and multi site hospitality businesses.
                            </p>
                            <p>
                                His focus is the operational layer of the business: how the property actually runs, the productivity of the team, the standards the guest experiences, and the contribution the operation delivers to the owner.
                            </p>
                            <p>
                                Through Optimum Hospitality Solutions, Sidney helps hotel owners, general managers and operations directors improve operational performance, productivity, profitability and guest satisfaction through practical work in the property, not theoretical advice.
                            </p>
                        </div>

                        <div className="mt-10 flex flex-wrap items-center gap-5">
                            <Link
                                to="/booking?type=discovery"
                                data-testid="founder-cta-booking"
                                className="inline-flex items-center gap-2 px-6 py-3 bg-brand-navy text-brand-stone text-sm tracking-wide hover:bg-brand-hover transition-colors"
                            >
                                Book a discovery call
                                <ArrowUpRight size={16} />
                            </Link>
                            <a
                                href="https://www.linkedin.com/in/sidney-a-a7ab35b7"
                                data-testid="founder-linkedin"
                                target="_blank"
                                rel="noopener noreferrer"
                                className="inline-flex items-center gap-2 text-sm text-brand-navy link-underline tracking-wide"
                            >
                                <Linkedin size={16} className="text-brand-champagne" />
                                Connect on LinkedIn
                            </a>
                        </div>
                    </div>
                </div>
            </section>

            {/* SELECTED EXPERIENCE & IMPACT */}
            <section className="border-b border-brand-border bg-brand-navy" data-testid="experience-section">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-14">
                        <div className="lg:col-span-7">
                            <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                                Selected Experience & Impact
                            </span>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] tracking-tight text-brand-stone">
                                A track record built on operations.
                            </h2>
                            <p className="mt-6 text-brand-stone/75 leading-relaxed max-w-xl">
                                Selected areas of hospitality leadership, operational delivery and improvement work, shaped by more than a decade in the property.
                            </p>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 lg:grid-cols-2 gap-x-12">
                        {EXPERIENCE.map((item, idx) => (
                            <div
                                key={item.title}
                                data-testid={`experience-item-${idx}`}
                                className="grid grid-cols-12 gap-5 items-start border-t border-brand-champagne/25 py-8 last:border-b lg:[&:nth-child(2)]:border-t lg:[&:nth-child(-n+2)]:border-t"
                            >
                                <div className="col-span-2 lg:col-span-2">
                                    <span className="font-display text-3xl text-brand-champagne">
                                        {String(idx + 1).padStart(2, "0")}
                                    </span>
                                </div>
                                <div className="col-span-10 lg:col-span-10">
                                    <h3 className="font-display text-xl text-brand-stone leading-snug">
                                        {item.title}
                                    </h3>
                                    <p className="mt-3 text-sm text-brand-stone/65 leading-relaxed">
                                        {item.body}
                                    </p>
                                </div>
                            </div>
                        ))}
                    </div>
                </div>
            </section>

            {/* WHY CHOOSE OPTIMUM */}
            <section className="border-b border-brand-border" data-testid="authority-section">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                                Why Choose Optimum
                            </span>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] tracking-tight text-brand-navy">
                                Built around the work, not the marketing.
                            </h2>
                            <p className="mt-6 text-brand-ink/75 leading-relaxed max-w-md">
                                Five reasons hotel owners, general managers and operations directors engage Optimum Hospitality Solutions.
                            </p>
                        </div>
                        <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-6">
                            {AUTHORITY.map((a, idx) => (
                                <div
                                    key={a.title}
                                    data-testid={`why-choose-${idx}`}
                                    className="border border-brand-border bg-brand-stone p-7 hover:border-brand-champagne transition-colors"
                                >
                                    <div className="flex items-start gap-4">
                                        <Award className="text-brand-champagne mt-1" size={20} strokeWidth={1.25} />
                                        <div>
                                            <h3 className="font-display text-lg text-brand-navy leading-snug">{a.title}</h3>
                                            <p className="mt-2 text-sm text-brand-ink/70 leading-relaxed">{a.body}</p>
                                        </div>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* FEATURED CASE STUDIES */}
            <section className="border-b border-brand-border bg-white" data-testid="case-studies-section">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 mb-12">
                        <div className="lg:col-span-7">
                            <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                                Featured Case Studies
                            </span>
                            <h2 className="font-display text-4xl sm:text-5xl lg:text-6xl mt-5 leading-[1.05] tracking-tight text-brand-navy">
                                Recent work, in three short stories.
                            </h2>
                            <p className="mt-6 text-brand-ink/75 leading-relaxed max-w-2xl">
                                Three representative engagements, each scoped around an operational problem the owner needed solved. Property names withheld; figures rounded.
                            </p>
                        </div>
                        <div className="lg:col-span-5 lg:flex lg:items-end lg:justify-end">
                            <Link
                                to="/case-studies"
                                data-testid="case-studies-view-all"
                                className="inline-flex items-center gap-2 text-sm text-brand-navy link-underline tracking-wide"
                            >
                                See all case studies <ArrowUpRight size={14} />
                            </Link>
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-px bg-brand-border">
                        {CASE_STUDIES.map((c, idx) => {
                            const Icon = [LineChart, ClipboardCheck, Smile][idx] || ClipboardCheck;
                            return (
                                <article
                                    key={c.slug}
                                    data-testid={`case-study-${c.slug}`}
                                    className="bg-brand-stone p-8 lg:p-10 flex flex-col"
                                >
                                    <Icon className="text-brand-champagne" size={26} strokeWidth={1.25} />
                                    <div className="mt-5 text-xs uppercase tracking-[0.2em] text-brand-ink/55">
                                        {c.property}
                                    </div>
                                    <h3 className="font-display text-xl mt-3 text-brand-navy leading-snug">
                                        {c.title}
                                    </h3>

                                    <div className="mt-6 space-y-5 text-sm leading-relaxed">
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne font-medium">Challenge</div>
                                            <p className="mt-1.5 text-brand-ink/80">{c.challenge}</p>
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne font-medium">Action Taken</div>
                                            <p className="mt-1.5 text-brand-ink/80">{c.action}</p>
                                        </div>
                                        <div>
                                            <div className="text-[10px] uppercase tracking-[0.25em] text-brand-champagne font-medium">Outcome</div>
                                            <p className="mt-1.5 text-brand-ink/80 font-medium">{c.outcome}</p>
                                        </div>
                                    </div>
                                </article>
                            );
                        })}
                    </div>

                    <p className="mt-8 text-xs text-brand-ink/55 italic max-w-3xl">
                        Illustrative case studies based on representative engagements. Full case studies with named properties will be published with the owner&rsquo;s permission.
                    </p>
                </div>
            </section>

            {/* OPIS teaser */}
            <section className="border-b border-brand-border bg-brand-navy" data-testid="opis-teaser-section">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
                    <div className="lg:col-span-8">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                            Introducing OPIS<sup className="text-[8px] ml-0.5">™</sup>
                        </span>
                        <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-5 leading-tight text-brand-stone">
                            Executive Decision Intelligence for hotels.
                        </h2>
                        <p className="mt-6 text-brand-stone/75 leading-relaxed max-w-2xl">
                            OPIS<sup className="text-[10px]">™</sup> is a structured executive review of your property across six operational pillars. Reviewed personally by a hospitality operator. Translated into a clear 90 day plan the leadership team can hold. Currently in Founding Member pilot.
                        </p>
                    </div>
                    <div className="lg:col-span-4 lg:text-right space-y-3">
                        <div>
                            <Link
                                to="/opis"
                                data-testid="home-opis-cta"
                                className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-champagne text-brand-navy text-sm tracking-wide hover:bg-white"
                            >
                                About OPIS<sup className="text-[8px]">™</sup> <ArrowUpRight size={16} />
                            </Link>
                        </div>
                        <div>
                            <Link
                                to="/opis/founding-members"
                                data-testid="home-opis-founding-link"
                                className="inline-flex items-center gap-2 text-sm text-brand-stone/85 link-underline"
                            >
                                Founding Member Programme
                            </Link>
                        </div>
                    </div>
                </div>
            </section>

            {/* HOW I WORK */}
            <section className="border-b border-brand-border bg-white" data-testid="approach-section">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-4">
                            <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                                Personal Approach
                            </span>
                            <h2 className="font-display text-4xl sm:text-5xl mt-5 leading-tight text-brand-navy">
                                How I work.
                            </h2>
                            <p className="mt-6 text-brand-ink/75 leading-relaxed">
                                A simple, sequenced engagement model, designed to deliver clarity quickly and translate insight into operational change.
                            </p>
                        </div>
                        <div className="lg:col-span-8 space-y-6">
                            {APPROACH.map((step, idx) => (
                                <div
                                    key={step.n}
                                    data-testid={`approach-step-${idx}`}
                                    className="grid grid-cols-12 gap-6 items-start border-b border-brand-border pb-7 last:border-0"
                                >
                                    <div className="col-span-2 lg:col-span-1 text-brand-champagne font-display text-2xl">{step.n}</div>
                                    <div className="col-span-10 lg:col-span-11">
                                        <h3 className="font-display text-xl text-brand-navy">{step.t}</h3>
                                        <p className="mt-2 text-brand-ink/75 leading-relaxed text-sm">{step.b}</p>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </div>
            </section>

            {/* PROFESSIONAL RECOMMENDATIONS */}
            <section className="border-b border-brand-border" data-testid="testimonials-section">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
                    <div className="max-w-2xl">
                        <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                            Professional Recommendations
                        </span>
                        <h2 className="font-display text-4xl sm:text-5xl mt-5 leading-tight text-brand-navy">
                            Voices from across the industry.
                        </h2>
                        <p className="mt-6 text-brand-ink/75 leading-relaxed">
                            Reflections from colleagues, clients and industry contacts who have worked alongside Sidney Adams.
                        </p>
                    </div>

                    {testimonials.length > 0 ? (
                        <div className="mt-14 grid grid-cols-1 md:grid-cols-3 gap-px bg-brand-border">
                            {testimonials.map((t, idx) => (
                                <figure
                                    key={t.id || `${t.name}-${idx}`}
                                    data-testid={`testimonial-${idx}`}
                                    className="bg-brand-stone p-8 lg:p-10 flex flex-col"
                                >
                                    <Quote className="text-brand-champagne" size={28} strokeWidth={1.25} />
                                    <blockquote className="mt-6 font-display text-lg leading-snug text-brand-navy">
                                        &ldquo;{t.quote}&rdquo;
                                    </blockquote>
                                    <figcaption className="mt-auto pt-8">
                                        <div className="text-sm font-medium text-brand-navy">{t.name}</div>
                                        <div className="text-xs text-brand-ink/60 mt-1">{t.role}</div>
                                    </figcaption>
                                </figure>
                            ))}
                        </div>
                    ) : (
                        <div className="mt-14 border border-brand-border bg-brand-stone p-10 lg:p-12 max-w-3xl" data-testid="testimonials-empty">
                            <Quote className="text-brand-champagne" size={28} strokeWidth={1.25} />
                            <p className="mt-6 font-display text-xl text-brand-navy leading-snug">
                                Recommendations from colleagues, clients and industry contacts will appear here as they are added.
                            </p>
                            <p className="mt-5 text-sm text-brand-ink/70 leading-relaxed">
                                Only recommendations submitted directly to us by people who have worked with Sidney are shown on this page. Nothing is illustrative or placeholder. If you have worked with Sidney and would like to share one, the form below takes a couple of minutes.
                            </p>
                        </div>
                    )}

                    <div className="mt-10 pt-8 border-t border-brand-border flex flex-col sm:flex-row sm:items-center justify-between gap-4">
                        <div className="max-w-xl">
                            <div className="text-xs uppercase tracking-[0.2em] text-brand-champagne">
                                Have you worked with Sidney?
                            </div>
                            <p className="mt-2 text-sm text-brand-ink/75 leading-relaxed">
                                Owners, asset managers, operators, colleagues and industry contacts are warmly invited to share a recommendation. Two or three sentences is plenty.
                            </p>
                        </div>
                        <Link
                            to="/recommend"
                            data-testid="testimonials-cta-recommend"
                            className="shrink-0 inline-flex items-center gap-2 px-6 py-3 bg-brand-navy text-brand-stone text-sm tracking-wide hover:bg-brand-hover transition-colors"
                        >
                            Share a recommendation
                        </Link>
                    </div>
                </div>
            </section>

            {/* BOOK A CONFIDENTIAL CONSULTATION — CTA */}
            <section className="bg-brand-navy" data-testid="work-with-us-section">
                <div className="mx-auto max-w-7xl px-6 lg:px-12 py-20 lg:py-28">
                    <div className="grid grid-cols-1 lg:grid-cols-12 gap-12">
                        <div className="lg:col-span-5">
                            <span className="text-xs uppercase tracking-[0.25em] text-brand-champagne font-medium">
                                Speak With Us
                            </span>
                            <h2 className="font-display text-3xl sm:text-4xl lg:text-5xl mt-5 leading-tight text-brand-stone">
                                Book a Confidential Consultation.
                            </h2>
                        </div>
                        <div className="lg:col-span-7">
                            <p className="text-lg leading-relaxed text-brand-stone/80">
                                Discuss your operational challenges, performance objectives and opportunities for improvement. Confidential from the first conversation, with no obligation to engage beyond the call.
                            </p>
                            <div className="mt-10 flex flex-wrap items-center gap-4">
                                <Link
                                    to="/booking"
                                    data-testid="work-with-us-cta-discovery"
                                    className="inline-flex items-center gap-2 px-7 py-3.5 bg-brand-champagne text-brand-navy text-sm tracking-wide hover:bg-white transition-colors"
                                >
                                    Book a Consultation
                                    <ArrowUpRight size={16} />
                                </Link>
                                <Link
                                    to="/contact"
                                    data-testid="work-with-us-cta-consultation"
                                    className="inline-flex items-center gap-2 px-7 py-3.5 border border-brand-champagne text-brand-stone text-sm tracking-wide hover:bg-brand-champagne hover:text-brand-navy transition-colors"
                                >
                                    Contact us
                                    <ArrowUpRight size={16} />
                                </Link>
                            </div>
                        </div>
                    </div>
                </div>
            </section>
        </div>
    );
}
